// besturimz-website/src/pages/Homepage.js
import React from 'react'
import styled from 'styled-components'
import HeroBanner from '../components/HeroBanner'
import FlightsService from '../components/FlightsService'
import AboutContact from '../components/AboutContact'
import {
  FaPlane,
  FaPlaneDeparture,
  FaPlaneArrival,
  FaPlaneSlash,
  FaTag,
  FaBus,
  FaRoute,
  FaCouch,
  FaClock,
  FaWifi,
  FaHotel,
  FaBed,
  FaMapMarkerAlt,
  FaConciergeBell,
  FaStar,
} from 'react-icons/fa'

const Section = styled.section`
  padding: 4rem 0;
  background: #f8f9fa;
  scroll-margin-top: 80px;

  @media (max-width: 768px) {
    padding: 3rem 0;
    scroll-margin-top: 70px;
  }
`

const SectionAlt = styled(Section)`
  background: #fff;
`

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: #333;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
`

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`

const FlightsSectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #333;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.25rem;
  }
`

const ABOUT_LOGO_CONTENT = { x: 82.21, y: 264.57, w: 430.58, h: 312.32 }
const ABOUT_LOGO_VIEWBOX = { w: 595.5, h: 842.25 }

const getAboutLogoMetrics = (iconHeight) => {
  const scale = iconHeight / ABOUT_LOGO_CONTENT.h
  return {
    wrapWidth: ABOUT_LOGO_CONTENT.w * scale,
    imageHeight: ABOUT_LOGO_VIEWBOX.h * scale,
    offsetLeft: -ABOUT_LOGO_CONTENT.x * scale,
    offsetTop: -ABOUT_LOGO_CONTENT.y * scale,
  }
}

const aboutLogoDesktop = getAboutLogoMetrics(168)
const aboutLogoMobile = getAboutLogoMetrics(128)

const AboutLogoWrap = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0 1.25rem;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`

const AboutLogoFrame = styled.div`
  position: relative;
  overflow: hidden;
  height: 168px;
  width: ${aboutLogoDesktop.wrapWidth}px;

  @media (max-width: 768px) {
    height: 128px;
    width: ${aboutLogoMobile.wrapWidth}px;
  }
`

const AboutLogo = styled.img`
  position: absolute;
  display: block;
  height: ${aboutLogoDesktop.imageHeight}px;
  width: auto;
  max-width: none;
  left: ${aboutLogoDesktop.offsetLeft}px;
  top: ${aboutLogoDesktop.offsetTop}px;
  pointer-events: none;

  @media (max-width: 768px) {
    height: ${aboutLogoMobile.imageHeight}px;
    left: ${aboutLogoMobile.offsetLeft}px;
    top: ${aboutLogoMobile.offsetTop}px;
  }
`

const AirlineLogos = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  align-items: center;
  gap: clamp(8px, 1.2vw, 16px);
  width: 100%;
  margin-bottom: 2.5rem;
  padding: 0.75rem 0 0.25rem;
`

const AirlineLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: clamp(32px, 4.5vw, 48px);
`

const AirlineLogoImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  transition: transform 0.25s ease;

  @media (hover: hover) {
    ${AirlineLogo}:hover & {
      transform: scale(1.12);
    }
  }
`

const AIRLINE_PARTNERS = [
  { name: 'Turkish Airlines', file: 'thy.png' },
  { name: 'Pegasus', file: 'pegasus.png' },
  { name: 'SunExpress', file: 'sunexpress.png' },
  { name: 'Ajet', file: 'Ajet.png' },
  { name: 'Qatar Airways', file: 'qatar.png' },
]

const Homepage = () => {
  const homepageSlides = [
    {
      id: 1,
      title: 'BESTURİZM ile Seyahatleriniz Kolaylaştırın',
      subtitle:
        '20 yılı aşan deneyimimizle tüm seyahatlerinizde güvenilir, hızlı ve ayrıcalıklı hizmetler sunamaya devam ediyoruz. Siz de ayrıcalıklı hizmetlerimizden yararlanabilmek için hemen bizimle iletişime geçin.',
      cta: 'İletişime Geç',
      ctaHref: '#footer',
      bgImage: '/slayt/ilk_slayt16_9.jpeg',
      bgImageMobile: '/slayt/ilk_slayt_9_16.jpeg',
      backgroundPosition: 'center center',
      backgroundPositionMobile: 'center center',
    },
    {
      id: 2,
      title: 'Uçak Bileti Rezervasyonu',
      subtitle:
        'En uygun fiyatlarla uçak biletinizi rezerve edin. Dünya çapında havayolu şirketleriyle anlaşmalı fiyatlarımızdan yararlanın.',
      cta: 'Hizmetlerimizi Keşfedin',
      ctaHref: '#flights',
      bgImage: `/slayt/${encodeURIComponent('uçak_16_9.jpg')}`,
      bgImageMobile: `/slayt/${encodeURIComponent('uçak_9_16.jpg')}`,
      backgroundPosition: 'center center',
      backgroundPositionMobile: 'center center',
    },
    {
      id: 3,
      title: 'Otel & Konaklama',
      subtitle:
        'Yurt içi ve yurt dışı otel rezervasyonlarınız için en uygun seçenekleri sunuyoruz. Konforlu ve güvenli konaklama deneyimi için bizimle iletişime geçin.',
      cta: 'Hizmetlerimizi Keşfedin',
      ctaHref: '#accommodation',
      bgImage: '/slayt/otel_16_9.png',
      bgImageMobile: '/slayt/otel_9_16.png',
      backgroundPosition: 'center center',
      backgroundPositionMobile: 'center center',
    },
  ]

  const icHatlarservices = [
    {
      icon: <FaPlane />,
      title: 'Ekonomi Class',
      description:
        "Konforlu ve ekonomik uçuş seçeneği. Bol bagaj hakkı ve temel hizmetler dahil. Türkiye'nin her yerine uçun.",
      highlight: false,
    },
    {
      icon: <FaPlaneDeparture />,
      title: 'Business Class',
      description:
        'Geniş koltuklar, özel check-in ve lounge hizmeti. İş seyahatleriniz için ideal lüks deneyim.',
      highlight: true,
    },
    {
      icon: <FaPlaneArrival />,
      title: 'Direkt Uçuş',
      description:
        'Hızlı ve rahat seyahat için direkt uçuş seçenekleri. Zaman kaybetmeden hedefinize ulaşın.',
      highlight: false,
    },
    {
      icon: <FaPlaneSlash />,
      title: 'Aktarmalı Uçuş',
      description:
        'Daha uygun fiyatlarla ulaşım. Aktarma molası ile farklı şehirleri keşfetme fırsatı.',
      highlight: false,
    },
  ]

  const icHatlarsfeatures = [
    { icon: <FaTag />, text: 'Erken Rezervasyon Avantajı' },
    { icon: <FaTag />, text: 'Miles & Smiles Puanları' },
    { icon: <FaTag />, text: 'Ücretsiz Bagaj' },
    { icon: <FaTag />, text: 'Esnek İptal Şartları' },
  ]

  const disHatlarservices = [
    {
      icon: <FaPlane />,
      title: 'Ekonomi Class',
      description:
        'Uluslararası seyahatler için ekonomik seçenek. Bol bagaj, yemek ve içecek hizmeti dahil.',
      highlight: false,
    },
    {
      icon: <FaPlaneDeparture />,
      title: 'Business Class',
      description:
        'Dünya çapında lüks seyahat. Yatak tipi koltuklar, özel menü ve VIP lounge erişimi.',
      highlight: true,
    },
    {
      icon: <FaPlaneArrival />,
      title: 'Direkt Uçuş',
      description: 'Aktarmasız, doğrudan hedefe ulaşım. Zaman tasarrufu ve konfor.',
      highlight: false,
    },
    {
      icon: <FaPlaneSlash />,
      title: 'Aktarmalı Uçuş',
      description:
        'Ucuz bilet seçenekleri. Farklı ülkeleri keşfedin ve yolculuğunuzu bir avantaja dönüştürün.',
      highlight: false,
    },
  ]

  const disHatlarsfeatures = [
    { icon: <FaTag />, text: 'Vize Desteği' },
    { icon: <FaTag />, text: 'Transfer Hizmeti' },
    { icon: <FaTag />, text: 'Seyahat Sigortası' },
    { icon: <FaTag />, text: '7/24 Destek' },
  ]

  const otbusServices = [
    {
      icon: <FaRoute />,
      title: 'Şehirlerarası Seferler',
      description:
        'Türkiye genelinde geniş hat ağı ile şehirlerarası otobüs bileti rezervasyonu. Güvenli ve konforlu yolculuk.',
      highlight: false,
    },
    {
      icon: <FaCouch />,
      title: 'Konforlu Koltuk Seçenekleri',
      description:
        '2+1 ve 2+2 düzeninde konforlu koltuk seçenekleri. Uzun yolculuklarda rahat bir seyahat deneyimi.',
      highlight: true,
    },
    {
      icon: <FaClock />,
      title: 'Esnek Saat Seçenekleri',
      description:
        'Günün her saatinde uygun sefer seçenekleri. Erken rezervasyon ile en uygun fiyatları yakalayın.',
      highlight: false,
    },
    {
      icon: <FaWifi />,
      title: 'Ek Hizmetler',
      description:
        'Wi-Fi, ikram ve bagaj hakkı gibi ek hizmetlerle yolculuğunuzu daha konforlu hale getirin.',
      highlight: false,
    },
  ]

  const otbusFeatures = [
    { icon: <FaTag />, text: 'Online Rezervasyon' },
    { icon: <FaTag />, text: 'Kolay İptal / Değişiklik' },
    { icon: <FaTag />, text: 'Güvenli Ödeme' },
    { icon: <FaTag />, text: '7/24 Destek' },
  ]

  const hotelServices = [
    {
      icon: <FaHotel />,
      title: 'Otel Rezervasyonu',
      description:
        'Türkiye ve yurt dışında geniş otel seçenekleriyle konforlu konaklama. Bütçenize ve tercihinize uygun oteller.',
      highlight: false,
    },
    {
      icon: <FaBed />,
      title: 'Her Bütçeye Uygun Seçenekler',
      description:
        'Ekonomik otellerden lüks tesislere kadar geniş yelpazede konaklama alternatifleri sunuyoruz.',
      highlight: true,
    },
    {
      icon: <FaMapMarkerAlt />,
      title: 'Merkezi Konumlar',
      description:
        'Şehir merkezine, havalimanına veya turistik noktalara yakın otellerle seyahatinizi kolaylaştırın.',
      highlight: false,
    },
    {
      icon: <FaConciergeBell />,
      title: 'Özel Konaklama Talepleri',
      description:
        'Aile, iş veya grup seyahatleriniz için özel oda ve paket seçenekleri. İhtiyaçlarınıza göre planlama.',
      highlight: false,
    },
  ]

  const hotelFeatures = [
    { icon: <FaStar />, text: 'Güvenilir Tesisler' },
    { icon: <FaTag />, text: 'En Uygun Fiyat Garantisi' },
    { icon: <FaTag />, text: 'Esnek İptal Seçenekleri' },
    { icon: <FaTag />, text: '7/24 Destek' },
  ]

  return (
    <div>
      <HeroBanner slides={homepageSlides} />

      <Section id="flights">
        <Container>
          <FlightsSectionTitle>Uçak Bileti Hizmetlerimiz</FlightsSectionTitle>
          <AirlineLogos>
            {AIRLINE_PARTNERS.map((partner) => (
              <AirlineLogo key={partner.file}>
                <AirlineLogoImage
                  src={`${process.env.PUBLIC_URL}/ortaklar/${partner.file}`}
                  alt={partner.name}
                  loading="lazy"
                />
              </AirlineLogo>
            ))}
          </AirlineLogos>
          <FlightsService
            title="İç Hatlar"
            subtitle="Türkiye içinde tüm şehirlere uygun fiyatlarla uçak bileti rezervasyonu yapın."
            services={icHatlarservices}
            features={icHatlarsfeatures}
            domestic={true}
          />
          <FlightsService
            title="Dış Hatlar"
            subtitle="Dünyanın her yerine uluslararası uçuş biletleri ve seyahat hizmetleri."
            services={disHatlarservices}
            features={disHatlarsfeatures}
            domestic={false}
          />
          <FlightsService
            title="Otobüs Bileti Hizmetlerimiz"
            subtitle="Türkiye genelinde şehirlerarası ve uluslararası otobüs bileti rezervasyonlarınız için her zaman hizmete hazırız. Konforlu ve güvenli yolculuk için bizimle iletişime geçin."
            services={otbusServices}
            features={otbusFeatures}
            domestic={false}
            icon={<FaBus />}
          />
        </Container>
      </Section>

      <SectionAlt id="accommodation">
        <Container>
          <SectionTitle>Otel & Konaklama</SectionTitle>
          <FlightsService
            title="Konaklama Hizmetlerimiz"
            subtitle="Yurt içi ve yurt dışı tüm seyahatinizin en keyifli kısmı için otel ve konaklama rezervasyonlarınızı güvenle yapın. Yurtiçi ve yurt dışı bütün otellerde size en uygun konaklama seçeneklerinden yararlanabilmek için bizimle iletişime geçin."
            services={hotelServices}
            features={hotelFeatures}
            domestic={true}
            icon={<FaHotel />}
          />
        </Container>
      </SectionAlt>

      <Section id="about">
        <Container>
          <AboutLogoWrap>
            <AboutLogoFrame>
              <AboutLogo
                src={`${process.env.PUBLIC_URL}/besturizm-logo-transparent.svg`}
                alt="Besturizm"
              />
            </AboutLogoFrame>
          </AboutLogoWrap>
          <AboutContact />
        </Container>
      </Section>
    </div>
  )
}

export default Homepage
